import { useCallback, useEffect, useRef, useState } from 'react';

import { doRequest } from '@r1-frontend/do-request';
import { ISuccessResponse } from '@r1-frontend/api-domru/phone-validator/validation/dto/ISuccessResponse';
import { send } from '@r1-frontend/api-domru/phone-validator/validation/send';
import { validate } from '@r1-frontend/api-domru/phone-validator/validation/validate';

import { dataLayerPush } from '@r1-frontend/analytics/dataLayerPush';

import { H3 } from '@r1-frontend/ui-react/components/typography/heading';
import { Paragraph4 } from '@r1-frontend/ui-react/components/typography/paragraph';
import { IndentContainer } from '@r1-frontend/ui-react/experimental/containers';
import { CATEGORIES, EVENTS } from '@r1-frontend/shared/constants/analytics';
import { useTimer } from '@r1-frontend/shared/hooks/useTimer';

import { ButtonWithTimer } from '~/src/components/ButtonWithTimer';
import { getFormattedPhoneNumber } from '~/src/helpers/phone';

import * as ST from './styled';

const CONFIRM_CODE_TYPE = 'sms';
const SMS_LENGTH = 4;

interface IProps {
    isOpen: boolean,
    phone: string,
    onConfirmed: (isConfirmed: boolean) => void,
    onClosePopup: () => void,
    dataLayerPushCategory?: string,
}

export const ConfirmNumberPopup = (
    {
        isOpen,
        phone,
        onConfirmed,
        onClosePopup,

        dataLayerPushCategory = CATEGORIES.fullBuy,
    }: IProps,
): JSX.Element => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isCodeSended, setIsCodeSended] = useState(false);
    const [isSmsRequestOnceSent, setIsSmsRequestOnceSent] = useState(true);
    const [smsCode, setSmsCode] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [errorSmsCode, setErrorSmsCode] = useState('');
    // флаг показывающий что апи вызывалось ранее (нужно понимание повторный ли это запроса кода/смс)
    const [apiHadCalled, setApiHadCalled] = useState<boolean>(false);
    const { timeStatus, seconds, start } = useTimer();

    const onSendSms = useCallback((confirmTypeLabel: string) => async() => {
        if (!isCodeSended) {
            dataLayerPush({
                event: EVENTS.UAevent,
                category: dataLayerPushCategory,
                action: 'click contact verification',
                label: confirmTypeLabel,
            });
        }
        setIsSmsRequestOnceSent(false);
        setIsLoading(true);

        setSmsCode('');

        const resp = await doRequest.phoneValidator<ISuccessResponse>(
            send({ phone, type: CONFIRM_CODE_TYPE }),
        );

        if (!apiHadCalled) {
            setApiHadCalled(true);
        } else {
            dataLayerPush({
                event: EVENTS.UAevent,
                category: dataLayerPushCategory,
                action: 'resend_confirm_code',
                label: CONFIRM_CODE_TYPE,
            });
        }

        if (resp.isSuccess) {
            setIsCodeSended(true);
        }
        setIsCodeSended(true);
        setIsLoading(false);
    }, [phone, CONFIRM_CODE_TYPE, isCodeSended]);

    const onValidateCode = useCallback(async(smsValue: string) => {
        setErrorMessage('');
        setIsLoading(true);

        const resp = await doRequest.phoneValidator<ISuccessResponse>(
            validate({
                phone,
                code: smsValue,
            }),
        );

        if (resp.isSuccess && resp.payload.success) {
            dataLayerPush({
                event: EVENTS.UAevent,
                category: dataLayerPushCategory,
                action: 'contact confirmed',
                label: CONFIRM_CODE_TYPE,
            });

            setSmsCode('');
            onConfirmed(true);
        }

        if ((resp.isSuccess && !resp.payload.success) || !resp.isSuccess) {
            setErrorMessage('Неверно. Попробуйте ещё раз.');
        }

        setIsLoading(false);
    }, [phone, CONFIRM_CODE_TYPE, onConfirmed]);

    const onInputSms = useCallback((event) => {
        const value: string = event.target.value;
        if (value.length <= SMS_LENGTH) {
            setSmsCode(value);
            setErrorSmsCode('');
            if (value.length === SMS_LENGTH) {
                onValidateCode(value);
            }
        }
    }, [onValidateCode]);

    useEffect(() => {
        if (inputRef?.current && isCodeSended) {
            inputRef.current.focus();
        }
    }, [inputRef, isCodeSended]);

    return (
        <ST.BasePopup isOpen={isOpen} onClose={onClosePopup}>
            <H3 $withIndent={false}>Введите код из смс</H3>

            <ST.SmsInputsWrap>
                <IndentContainer $united={[0, 0, 16]}>
                {errorSmsCode
                    ? <Paragraph4>{errorSmsCode}</Paragraph4>
                    : <Paragraph4>
                        {'Отправили смс на номер: '}
                        <ST.SmsTextNumber>{getFormattedPhoneNumber(phone)}</ST.SmsTextNumber>
                    </Paragraph4>}
                </IndentContainer>
                <ST.SmsInput
                    ref={inputRef}
                    value={smsCode}
                    onChange={onInputSms}
                    placeholder={'Код'}
                    errorMessage={errorMessage}
                    isNotValid={smsCode.length === SMS_LENGTH && !!errorMessage}
                />
            </ST.SmsInputsWrap>

            <ST.BtnWrap>
                <ButtonWithTimer
                    status={timeStatus}
                    isLoading={isLoading}
                    onBtnClick={onSendSms(CONFIRM_CODE_TYPE)}
                    onStart={start}
                    autoStart={isSmsRequestOnceSent}
                    seconds={seconds}
                />
            </ST.BtnWrap>
        </ST.BasePopup>
    );
};
